import { useState } from "react";

import { useTranslation } from "react-i18next";

import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import { ApiResources } from "@/api/resources";
import useTableItemDetails from "@/components/DetailsSection/hooks/useTableItemDetails";
import { IntegrationDetailsDto } from "@/components/DetailsSection/types";
import Dialog from "@/components/Dialog/Dialog";
import { DialogWidth } from "@/components/Dialog/types/enums";
import IntegrationDialogContent from "@/components/PageHeader/actions/dialogs/integrations/IntegrationDialogContent";
import ConfirmDeactivateResource from "@/components/PageHeader/actions/dialogs/reusableDialogs/ConfirmDeactivateResource/ConfirmDeactivateResource";
import { ALERT_STATUS } from "@/context/alert/types";
import { useIntegrationsOptions } from "@/Pages/Integrations/hooks/useIntegrationsOptions";
import {
  INTEGRATION_FIELD_NAMES,
  IntegrationErrors,
  IntegrationObjectState,
  NameValuePair
} from "@/Pages/Integrations/types";
import {
  IntegrationsErrorDialogSteps,
  ROUTES,
  useMediaQueries
} from "@/shared";
import DialogActions from "@/shared/components/buttons/DialogActions";
import { refreshTableAndUpdateDetails } from "@/shared/functions/functions";
import useResponse from "@/shared/hooks/useResponse";
import { useIntegrationsDialogStore } from "@/stores/useIntegrationsDialogStore";
import { useIntegrationsTableStore } from "@/stores/useTableStore";

interface EditIntegrationDialogProps {
  rowData: IntegrationDetailsDto;
  isOpen: boolean;
  onClose: () => void;
}

const EditIntegrationDialog = ({
  rowData,
  isOpen,
  onClose
}: EditIntegrationDialogProps) => {
  const { toMd } = useMediaQueries();
  const { t } = useTranslation();
  const { putData } = useAxiosPrivate();
  const { getTableItemDetails } = useTableItemDetails();
  const { handleResponse, handleCommonError } = useResponse();
  const { sourceStep, fromError, showError } = useIntegrationsDialogStore();

  const params = useIntegrationsTableStore(state => state.params);
  const { contains: searchParams, selectedTableItem } = params;

  const { driverFileDestinationOptions, vehicleFolderNamingOptions } =
    useIntegrationsOptions();

  const getObjectByValue = (value: string, options: NameValuePair[]) => {
    return options.find(option => option.value === value)!;
  };

  const {
    vehicleFolderNaming,
    driverFolderNaming,
    company,
    fileFormat,
    name,
    host,
    port,
    directoryPath,
    username,
    includeSubCompanies,
    syncHistoricalFiles,
    enabled
  } = rowData;

  const vehicleFolderNamingObj = getObjectByValue(
    vehicleFolderNaming,
    vehicleFolderNamingOptions
  );

  const driverFolderNamingObj = getObjectByValue(
    driverFolderNaming,
    driverFileDestinationOptions
  );

  const fileFormatObj = {
    name: t(`Settings##file formats##${fileFormat}`),
    value: fileFormat
  };

  const initialIntegrationObject: IntegrationObjectState = {
    [INTEGRATION_FIELD_NAMES.SelectedCompany]: company || null,
    [INTEGRATION_FIELD_NAMES.Name]: name,
    [INTEGRATION_FIELD_NAMES.FileFormat]: fileFormatObj,
    [INTEGRATION_FIELD_NAMES.VehicleFolderNaming]: vehicleFolderNamingObj,
    [INTEGRATION_FIELD_NAMES.DriverFolderNaming]: driverFolderNamingObj,
    [INTEGRATION_FIELD_NAMES.Host]: host,
    [INTEGRATION_FIELD_NAMES.Port]: port,
    [INTEGRATION_FIELD_NAMES.RemotePath]: directoryPath || "",
    [INTEGRATION_FIELD_NAMES.Username]: username,
    [INTEGRATION_FIELD_NAMES.Password]: "",
    [INTEGRATION_FIELD_NAMES.IncludeSubCompanies]: includeSubCompanies,
    [INTEGRATION_FIELD_NAMES.SyncHistoricalFiles]: syncHistoricalFiles,
    [INTEGRATION_FIELD_NAMES.Enabled]: enabled
  };

  const [integrationsObject, setIntegrationsObject] =
    useState<IntegrationObjectState>(initialIntegrationObject);

  const initialErrors: IntegrationErrors = {
    [INTEGRATION_FIELD_NAMES.Name]: "",
    [INTEGRATION_FIELD_NAMES.Host]: "",
    [INTEGRATION_FIELD_NAMES.Port]: "",
    [INTEGRATION_FIELD_NAMES.RemotePath]: "",
    [INTEGRATION_FIELD_NAMES.Username]: "",
    [INTEGRATION_FIELD_NAMES.Password]: ""
  };

  const [errors, setErrors] = useState<IntegrationErrors>(initialErrors);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasInteracted, setHasInteracted] = useState<boolean>(false);

  const [isConfirmDeactivateOpen, setIsConfirmDeactivateOpen] = useState(false);
  const [hasUpdatedEnable, setHasUpdatedEnable] = useState(false);

  const hasErrors = Object.values(errors).some(error => error !== "");

  const optionalFields = new Set([
    INTEGRATION_FIELD_NAMES.RemotePath,
    INTEGRATION_FIELD_NAMES.IncludeSubCompanies,
    INTEGRATION_FIELD_NAMES.SyncHistoricalFiles,
    INTEGRATION_FIELD_NAMES.Password
  ]);

  const requiredFieldsFilled = Object.entries(integrationsObject).every(
    ([key, value]) => {
      if (optionalFields.has(key as INTEGRATION_FIELD_NAMES)) return true;
      return (
        value !== null && value !== undefined && String(value).trim() !== ""
      );
    }
  );

  const successMessage = t("Alert##edit integration success");

  const resetAllValues = () => {
    setIntegrationsObject(initialIntegrationObject);
    setErrors(initialErrors);
    setHasInteracted(false);
  };

  const handleEditSubmit = async (skipConfirm?: boolean) => {
    const isDeactivateIdentical =
      initialIntegrationObject[INTEGRATION_FIELD_NAMES.Enabled] ===
      integrationsObject[INTEGRATION_FIELD_NAMES.Enabled];

    if (
      !skipConfirm &&
      !isDeactivateIdentical &&
      hasUpdatedEnable &&
      !integrationsObject[INTEGRATION_FIELD_NAMES.Enabled]
    ) {
      setIsConfirmDeactivateOpen(true);
      return;
    }
    setIsLoading(true);
    const editableItemHasOpenedDetails = selectedTableItem === rowData.id;

    try {
      const { selectedCompany, ...cleanedIntegrationsObject } =
        integrationsObject;

      const payloadObject = {
        ...cleanedIntegrationsObject,
        id: rowData.id,
        companyId: integrationsObject.selectedCompany?.id,
        driverFolderNaming: integrationsObject.driverFolderNaming?.value,
        vehicleFolderNaming: integrationsObject.vehicleFolderNaming?.value,
        fileFormat: integrationsObject.fileFormat?.value
      };

      const { data } = await putData(
        ApiResources.IntegrationsSftp,
        payloadObject
      );

      const errorCodeExists = data?.connectionErrorCode
        ? undefined
        : successMessage;

      refreshTableAndUpdateDetails({
        valueInSearchField: searchParams,
        resourceId: data.id,
        successMessage: errorCodeExists,
        itemHasOpenedDetails: editableItemHasOpenedDetails,
        getTableItemDetails,
        handleResponse
      });

      if (data.connectionErrorCode) {
        showError(
          data.connectionErrorCode,
          data,
          IntegrationsErrorDialogSteps.Edit
        );

        setIntegrationsObject(prev => {
          return {
            ...prev,
            [INTEGRATION_FIELD_NAMES.Password]: ""
          };
        });
      } else {
        handleResponse(ALERT_STATUS.Success, successMessage);
        onClose();
        resetAllValues();
      }
    } catch (error: any) {
      handleCommonError(error, t);
    }

    setIsLoading(false);
  };

  const handleConfirmSubmit = () => {
    handleEditSubmit(true);
    setIsConfirmDeactivateOpen(false);
  };

  const handleClose = () => {
    if (!isLoading) {
      if (fromError) {
        const warningMessage = t("Alert##integration editing cancelled");

        sourceStep === IntegrationsErrorDialogSteps.Retry
          ? handleResponse(ALERT_STATUS.Warning, warningMessage)
          : handleResponse(ALERT_STATUS.Success, successMessage);
      }

      onClose();
    }
  };

  const isReadyToConfirm = !hasErrors && requiredFieldsFilled && hasInteracted;

  return (
    <>
      <Dialog
        data-testid="edit-integration-dialog"
        title={t("Dialog##sftp integration##edit title", {
          integrationName:
            initialIntegrationObject[INTEGRATION_FIELD_NAMES.Name]
        })}
        isTitleSeparator
        isActionsSeparator
        extendToMaxWidth={toMd}
        open={isOpen}
        close={handleClose}
        submit={handleEditSubmit}
        width={toMd ? undefined : DialogWidth.Dialog920px}
        customPaperMargin="48px"
        actions={
          <DialogActions
            onClose={handleClose}
            isLoading={isLoading}
            disabledClose={isLoading}
            loaderTitle={t("Button##testing connection")}
            isreadyToConfirm={isReadyToConfirm}
            confirmationButtonText={t("Button##save")}
            confirmationButtonTestId="edit-save-integration-button"
            cancelButtonText={t("Button##close")}
            cancelButtonTestId="edit-close-integration-button"
          />
        }
      >
        <IntegrationDialogContent
          optionalPassword
          isLoading={isLoading}
          integrationsObject={integrationsObject}
          errors={errors}
          readOnlyFields={[INTEGRATION_FIELD_NAMES.SelectedCompany]}
          hasInteracted={hasInteracted}
          hasUpdatedEnable={hasUpdatedEnable}
          setHasUpdatedEnable={setHasUpdatedEnable}
          setHasInteracted={setHasInteracted}
          setErrors={setErrors}
          setIntegrationsObject={setIntegrationsObject}
        />
      </Dialog>

      <ConfirmDeactivateResource
        isOpen={isConfirmDeactivateOpen}
        route={ROUTES.Integrations}
        resourceName={integrationsObject[INTEGRATION_FIELD_NAMES.Name]}
        onClose={() => setIsConfirmDeactivateOpen(false)}
        onSubmit={handleConfirmSubmit}
      />
    </>
  );
};

export default EditIntegrationDialog;
